import { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatBytes, formatDate } from 'client/js/util/formatters';

const FileItem = ({ item, isSelected, onSelect, onItemClick, onDoubleClick, onContextMenu, paneId, currentPath, selectedItems, onDropOnItem, isRenaming, onRenameCancel, onRenameSubmit }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [newName, setNewName] = useState(item.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isRenaming) {
      setNewName(item.name);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [isRenaming, item.name]);

  const handleKeyDown = (e) => {
    // Stop propagation to prevent FileBrowser navigation (e.g. Backspace)
    e.stopPropagation();

    if (e.key === 'Enter') {
      e.preventDefault();
      onRenameSubmit(item, newName);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel();
    }
  };

  const handleBlur = () => {
    onRenameSubmit(item, newName);
  };

  const getIcon = () => {
    if (item.is_symlink) {
      return 'fa-link';
    }
    if (item.type === 'directory') {
      return 'fa-folder';
    }

    // Determine icon based on file type
    const ext = item.name.split('.').pop().toLowerCase();

    // Images
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif'];
    if (imageExtensions.includes(ext)) {
      return 'fa-file-image-o';
    }

    // Code files
    const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'php', 'py', 'rb', 'java', 'c', 'cpp', 'h', 'hpp', 'go', 'rs', 'swift', 'kt'];
    if (codeExtensions.includes(ext)) {
      return 'fa-file-code-o';
    }

    // Web files
    if (['html', 'htm', 'css', 'scss', 'sass', 'less'].includes(ext)) {
      return 'fa-file-code-o';
    }

    // Text/Config files
    if (['txt', 'text', 'log', 'conf', 'config', 'ini', 'cfg', 'env', 'md', 'markdown'].includes(ext)) {
      return 'fa-file-text-o';
    }

    // Archives
    if (['zip', 'rar', 'tar', 'gz', '7z', 'bz2'].includes(ext)) {
      return 'fa-file-archive-o';
    }

    // PDFs
    if (ext === 'pdf') {
      return 'fa-file-pdf-o';
    }

    // Audio
    if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext)) {
      return 'fa-file-audio-o';
    }

    // Video
    if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) {
      return 'fa-file-video-o';
    }

    // Excel/Spreadsheets
    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
      return 'fa-file-excel-o';
    }

    // Word/Documents
    if (['doc', 'docx', 'odt'].includes(ext)) {
      return 'fa-file-word-o';
    }

    // Default file icon
    return 'fa-file-o';
  };

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';

    // If the current item is selected, drag all selected items
    // Otherwise, just drag this item
    const itemsToDrag = isSelected && selectedItems.length > 0
      ? selectedItems
      : [item];

    e.dataTransfer.setData('application/json', JSON.stringify({
      items: itemsToDrag,
      sourcePaneId: paneId,
      sourcePath: currentPath
    }));
  };

  const handleDragOver = (e) => {
    if (item.type === 'directory') {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (item.type !== 'directory') return;

    if (onDropOnItem) {
      onDropOnItem(e, item);
    }
  };

  return (
    <tr
      className={`file-item ${isSelected ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
      onClick={(e) => onItemClick(e)}
      onDoubleClick={onDoubleClick}
      onContextMenu={(e) => {
        e.stopPropagation();
        onContextMenu(e);
      }}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <td>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {
            // Empty onChange to make React happy (controlled component)
          }}
          onMouseDown={(e) => {
            if (e.shiftKey) {
              // For shift+click, prevent the default mousedown to avoid checkbox state change
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            if (e.shiftKey) {
              // For shift+click, prevent default and let it bubble to row
              e.preventDefault();
              // Let it propagate to the row for range selection
            } else {
              // For normal clicks, toggle selection and stop propagation
              e.stopPropagation();
              onSelect(!isSelected);
            }
          }}
        />
      </td>
      <td>
        <i className={`fa ${getIcon()}`} style={item.is_symlink ? { color: '#6c757d' } : {}} />
      </td>
      <td className="file-name">
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {item.name}
            {item.is_symlink && item.symlink_target && (
              <small className="text-muted ml-2">
                → {item.symlink_target}
              </small>
            )}
          </>
        )}
      </td>
      <td className="text-right">{item.type === 'directory' ? '-' : formatBytes(item.size)}</td>
      <td className="text-monospace">{item.mode}</td>
      <td>{formatDate(item.mtime)}</td>
    </tr>
  );
};

FileItem.propTypes = {
  item: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  paneId: PropTypes.string.isRequired,
  currentPath: PropTypes.string.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onDropOnItem: PropTypes.func,
  isRenaming: PropTypes.bool,
  onRenameCancel: PropTypes.func,
  onRenameSubmit: PropTypes.func,
};

export default memo(FileItem);
